/**
 * MECE Pillar Widget (DC-1367)
 *
 * Shows pillars with sub-scores and station sources.
 * DC-325: Enhanced with AI Readiness features (profile selection, blockers, sampling).
 * Extracted from StrategicIntelligenceDashboard.tsx.
 */

import React, { useState, useEffect } from 'react';
import { ProfileSelector, type AssessmentProfile } from '../aiReadiness/ProfileSelector';
import { SamplingIndicator } from '../aiReadiness/SamplingIndicator';
import { useJourneyId } from '../../contexts/JourneyContext';
import type { TechnicalAssetScore, AIReadinessScore } from './types';
import { getScoreColor } from './helpers';

interface MecePillarWidgetProps {
  pillars: TechnicalAssetScore['pillars'] | undefined;
  journeyId?: string;
}

export const MecePillarWidget: React.FC<MecePillarWidgetProps> = ({ pillars, journeyId: journeyIdProp }) => {
  const journeyId = useJourneyId(journeyIdProp);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [_selectedProfile, setSelectedProfile] = useState<AssessmentProfile | null>(null);
  const [aiReadinessScore, setAiReadinessScore] = useState<AIReadinessScore | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [samplingUsed, setSamplingUsed] = useState(false);
  const [sampleSize, setSampleSize] = useState<number | null>(null);
  const [totalRows, setTotalRows] = useState<number | null>(null);
  const [showBlockers, setShowBlockers] = useState(false);

  const openGuide = async () => {
    try {
      await fetch('/open-guide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guidePath: 'MECE_Pillars_Card_Guide.md' })
      });
    } catch (err) {
      console.error('[MecePillars] Failed to open guide:', err);
    }
  };

  useEffect(() => {
    if (journeyId) {
      fetchExistingAssessment();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId]);

  const fetchExistingAssessment = async () => {
    if (!journeyId) return;
    try {
      const response = await fetch(`/api/ai-readiness/${journeyId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.assessment) {
          setAiReadinessScore({
            overall_score: data.assessment.overall_score,
            grade: data.assessment.grade,
            pillars: data.assessment.pillar_details,
            weights_used: data.assessment.weights_used,
            blockers: data.assessment.blockers || [],
            confidence: data.assessment.confidence || 'medium',
          });
          setSamplingUsed(data.assessment.sampling_used || false);
          setSampleSize(data.assessment.sample_size);
          setTotalRows(data.assessment.total_rows);
        }
      }
    } catch (err) {
      console.error('[MecePillars] Error fetching AI Readiness:', err);
    }
  };

  const handleProfileChange = (profileId: string | null, profile: AssessmentProfile | null) => {
    setSelectedProfileId(profileId);
    setSelectedProfile(profile);
  };

  const handleRecalculate = async () => {
    if (!journeyId) return;
    setCalculating(true);
    try {
      const response = await fetch('/api/ai-readiness/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          journeyId,
          profileId: selectedProfileId,
          saveToDatabase: true,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.score) {
          setAiReadinessScore(data.score);
          setSamplingUsed(data.samplingUsed || false);
          setSampleSize(data.sampleSize);
          setTotalRows(data.totalRows);
        }
      }
    } catch (err) {
      console.error('[MecePillars] Error calculating AI Readiness:', err);
    } finally {
      setCalculating(false);
    }
  };

  const getBlockerColor = (severity: string): string => {
    switch (severity) {
      case 'critical': return 'var(--tri-hard)';
      case 'high': return 'var(--tri-terra)';
      case 'medium': return 'var(--tri-soft)';
      case 'low': return 'var(--tri-teal)';
      default: return 'var(--tri-slate)';
    }
  };

  if (!pillars) {
    return (
      <div className="mece-pillars-widget mece-pillars-widget--empty">
        <p>Complete station analyses to see MECE breakdown</p>
      </div>
    );
  }

  const pillarConfig = [
    {
      key: 'statistical_health',
      label: 'Statistical Health',
      weight: '40%',
      data: pillars.statistical_health,
      subItems: [
        { label: 'Data Quality', station: 'data_quality', score: pillars.statistical_health?.data_quality?.score },
        { label: 'ML Readiness', station: 'ml_readiness', score: pillars.statistical_health?.ml_readiness?.score },
      ],
    },
    {
      key: 'semantic_maturity',
      label: 'Semantic Clarity',
      weight: '30%',
      data: pillars.semantic_maturity,
      subItems: [
        { label: 'Ontology Inference', station: 'ontology_inference', score: pillars.semantic_maturity?.ontology_coverage ?? pillars.semantic_maturity?.score },
      ],
    },
    {
      key: 'governance_fair',
      label: 'Governance & Compliance',
      weight: '30%',
      data: pillars.governance_fair,
      subItems: [
        { label: 'FAIR Assessment', station: 'fair_assessment', score: pillars.governance_fair?.fair_rules?.normalized ?? pillars.governance_fair?.fair_rules?.score },
        { label: 'Local Governance', station: 'data_governance', score: pillars.governance_fair?.local_governance?.score },
      ],
    },
  ];

  const criticalBlockers = aiReadinessScore?.blockers?.filter(
    b => b.severity === 'critical' || b.severity === 'high'
  ) || [];

  return (
    <div className="mece-pillars-widget">
      <div className="mece-header">
        <span className="mece-icon">📐</span>
        <h3 className="mece-title">MECE Pillars</h3>
        <span className="mece-badge">No Overlap</span>
        <button
          className="help-button"
          onClick={openGuide}
          title="Open MECE Pillars Guide"
          aria-label="Help: MECE Pillars"
        >
          ❓
        </button>
      </div>

      {journeyId && (
        <div className="mece-profile-section">
          <ProfileSelector
            selectedProfileId={selectedProfileId}
            onProfileChange={handleProfileChange}
            className="mece-profile-selector"
          />
        </div>
      )}

      <div className="mece-pillars-list">
        {pillarConfig.map((pillar) => (
          <div key={pillar.key} className="mece-pillar-item">
            <div className="pillar-main-row">
              <div className="pillar-info">
                <span className="pillar-name">{pillar.label}</span>
                <span className="pillar-weight">{pillar.weight}</span>
              </div>
              <div className="pillar-bar-container">
                <div
                  className="pillar-bar-fill"
                  style={{
                    width: `${pillar.data?.score || 0}%`,
                    backgroundColor: getScoreColor(pillar.data?.score || 0),
                  }}
                />
              </div>
              <span
                className="pillar-score"
                style={{ color: getScoreColor(pillar.data?.score || 0) }}
              >
                {Math.round(pillar.data?.score || 0)}
              </span>
            </div>

            <div className="pillar-sub-items">
              {pillar.subItems.map((sub, idx) => (
                <div key={idx} className="pillar-sub-row">
                  <span className="sub-connector">└─</span>
                  <span className="sub-label">{sub.label}</span>
                  <span className="sub-score">
                    {sub.score != null ? Math.round(sub.score) : '--'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {criticalBlockers.length > 0 && (
        <div className="mece-blockers-section">
          <button
            className="mece-blockers-toggle"
            onClick={() => setShowBlockers(!showBlockers)}
          >
            <span className="blockers-icon">🚫</span>
            <span className="blockers-count">{criticalBlockers.length} Blockers</span>
            <span className="blockers-arrow">{showBlockers ? '▼' : '▶'}</span>
          </button>
          {showBlockers && (
            <div className="mece-blockers-list">
              {criticalBlockers.slice(0, 3).map((blocker) => (
                <div
                  key={blocker.id}
                  className="mece-blocker-item"
                  style={{ borderLeftColor: getBlockerColor(blocker.severity) }}
                >
                  <span
                    className="blocker-severity"
                    style={{ color: getBlockerColor(blocker.severity) }}
                  >
                    {blocker.severity.toUpperCase()}
                  </span>
                  <span className="blocker-message">{blocker.message}</span>
                </div>
              ))}
              {criticalBlockers.length > 3 && (
                <p className="blockers-more">+{criticalBlockers.length - 3} more blockers</p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mece-footer">
        <p className="mece-note">Each metric appears in exactly one pillar</p>

        {journeyId && (
          <div className="mece-footer-actions">
            <SamplingIndicator
              samplingUsed={samplingUsed}
              sampleSize={sampleSize}
              totalRows={totalRows}
              className="mece-sampling"
            />
            <button
              className="mece-recalculate-btn"
              onClick={handleRecalculate}
              disabled={calculating}
              title="Recalculate AI Readiness with selected profile"
            >
              {calculating ? '⏳' : '🔄'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
